import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Building2, ChevronRight, Folder, Package, Pencil, Plus, QrCode, Trash2 } from "lucide-react";
import { rpc } from "../../lib/rpc";
import { sortByNameThenCode } from "../../lib/naturalSort";
import { AdminPageHeader } from "../../components/admin/DataTable";
import { CompartmentQrDialog } from "../../components/admin/CompartmentQrDialog";
import { Button } from "../../components/ui/button";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";

export default function AdminLagerstruktur() {
    const [locations, setLocations] = useState([]);
    const [rooms, setRooms] = useState([]);
    const [shelves, setShelves] = useState([]);
    const [compartments, setCompartments] = useState([]);
    const [expanded, setExpanded] = useState({});
    const [selected, setSelected] = useState(null);
    const [qrOpen, setQrOpen] = useState(false);
    const [createMessage, setCreateMessage] = useState("");

    const shelfMap = useMemo(() => Object.fromEntries(shelves.map((s) => [s.id, s])), [shelves]);
    const roomMap = useMemo(() => Object.fromEntries(rooms.map((r) => [r.id, r])), [rooms]);
    const sortedLocations = useMemo(() => sortByNameThenCode(locations), [locations]);
    const sortedRooms = useMemo(() => sortByNameThenCode(rooms), [rooms]);
    const sortedShelves = useMemo(() => sortByNameThenCode(shelves), [shelves]);
    const sortedCompartments = useMemo(() => sortByNameThenCode(compartments), [compartments]);

    const qrItems = useMemo(
        () =>
            // Etikett: nur QR + Klarname des Fachs (Wunsch Hauptnutzer 2026-08-23,
            // #114). Raum/Regal/Code bleiben zur Auswahl im Dialog sichtbar.
            sortedCompartments.map((c) => {
                const shelf = shelfMap[c.shelf_id];
                const room = shelf ? roomMap[shelf.room_id] : null;
                return {
                    id: c.id,
                    title: c.name || c.code,
                    code: c.code,
                    scan_number: c.scan_number,
                    hint: [room?.name, shelf?.name, c.code].filter(Boolean).join(" · "),
                };
            }),
        [sortedCompartments, shelfMap, roomMap]
    );

    const load = () =>
        Promise.all([
            rpc.get("/master/locations"),
            rpc.get("/master/rooms"),
            rpc.get("/master/shelves"),
            rpc.get("/master/compartments"),
        ]).then(([l, r, s, c]) => {
            if (l.ok) setLocations(l.data);
            if (r.ok) setRooms(r.data);
            if (s.ok) setShelves(s.data);
            if (c.ok) setCompartments(c.data);
        });
    useEffect(() => { load(); }, []);

    const toggle = (key) => setExpanded((e) => ({ ...e, [key]: !e[key] }));

    // #136 Befund 3: Dialog mit Formular statt prompt(), mit Erfolgs-Feedback.
    const [showAddLocation, setShowAddLocation] = useState(false);
    // #158: Raum/Regal/Fach in der UI anlegen und Knoten löschen (Kundenbedarf).
    const [addNode, setAddNode] = useState(null); // {kind: 'room'|'shelf'|'compartment', parent}
    const [deleteNode, setDeleteNode] = useState(null); // {kind, doc}

    const DELETE_META = {
        location: { label: "Standort", path: "locations", cascade: "alle enthaltenen Räume, Regale und Fächer" },
        room: { label: "Raum", path: "rooms", cascade: "alle enthaltenen Regale und Fächer" },
        shelf: { label: "Regal", path: "shelves", cascade: "alle enthaltenen Fächer" },
        compartment: { label: "Fach", path: "compartments", cascade: null },
    };

    const runDelete = async () => {
        const { kind, doc } = deleteNode;
        setDeleteNode(null);
        const res = await rpc.del(`/master/${DELETE_META[kind].path}/${doc.id}`);
        if (!res.ok) return toast.error(res.error);
        toast.success(`${DELETE_META[kind].label} gelöscht.`);
        if (selected?.doc && (kind === "compartment" ? selected.doc.id === doc.id : true)) {
            setSelected(null);
        }
        load();
    };

    const UPDATE_META = {
        location: { label: "Standort", path: "locations" },
        room: { label: "Raum", path: "rooms" },
        shelf: { label: "Regal", path: "shelves" },
        compartment: { label: "Fach", path: "compartments" },
    };

    const saveNode = async (kind, doc, patch) => {
        const meta = UPDATE_META[kind];
        const res = await rpc.patch(`/master/${meta.path}/${doc.id}`, patch);
        if (!res.ok) return res;
        await load();
        return { ...res, message: `${meta.label} aktualisiert.` };
    };

    return (
        <>
            <AdminPageHeader title="Lagerstruktur" subtitle="Standorte → Räume → Regale → Fächer">
                <Button variant="outline" onClick={() => setQrOpen(true)} data-testid="lager-qr-print" disabled={compartments.length === 0}>
                    <QrCode className="mr-1 h-4 w-4" /> QR-Codes anzeigen
                </Button>
                <Button onClick={() => setShowAddLocation(true)} data-testid="lager-add-location">
                    <Plus className="mr-1 h-4 w-4" /> Neuer Standort
                </Button>
            </AdminPageHeader>

            {createMessage ? <p role="status" className="mb-3 text-sm text-foreground" data-testid="lager-create-status">{createMessage}</p> : null}
            <div className="grid grid-cols-12 items-start gap-6">
                <aside className="col-span-12 min-w-0 overflow-auto rounded-lg border border-border bg-card/40 p-3 lg:col-span-5 lg:max-h-[calc(100dvh-14rem)]" data-testid="lager-tree" aria-label="Lagerorte">
                    {locations.length === 0 ? (
                        <div className="py-6 text-center text-xs text-muted-foreground">
                            Noch keine Standorte. Lege oben den ersten an.
                        </div>
                    ) : (
                        sortedLocations.map((loc) => (
                            <div key={loc.id}>
                                <div className="flex items-center gap-1">
                                    <button
                                        type="button"
                                        onClick={() => toggle(`l-${loc.id}`)}
                                        className="flex min-h-11 min-w-0 flex-1 items-center gap-2 text-sm text-foreground"
                                        data-testid={`lager-loc-${loc.id}`}
                                    >
                                        <Building2 className="h-4 w-4 shrink-0 text-primary" />
                                        <span className="truncate">{loc.name}</span>
                                        <ChevronRight className={`ml-auto h-3 w-3 shrink-0 ${expanded[`l-${loc.id}`] ? "rotate-90" : ""}`} />
                                    </button>
                                    <NodeActions
                                        addLabel="Raum hinzufügen"
                                        onAdd={() => setAddNode({ kind: "room", parent: loc })}
                                        editLabel={`Standort ${loc.name} bearbeiten`}
                                        onEdit={() => setSelected({ kind: "location", doc: loc })}
                                        deleteLabel={`Standort ${loc.name} löschen`}
                                        onDelete={() => setDeleteNode({ kind: "location", doc: loc })}
                                        testId={`lager-loc-actions-${loc.id}`}
                                    />
                                </div>
                                {expanded[`l-${loc.id}`] &&
                                    sortedRooms.filter((r) => r.location_id === loc.id).map((room) => (
                                        <div key={room.id} className="ml-5">
                                            <div className="flex items-center gap-1">
                                                <button
                                                    type="button"
                                                    onClick={() => toggle(`r-${room.id}`)}
                                                    className="flex min-h-11 min-w-0 flex-1 items-center gap-2 text-xs text-foreground"
                                                    data-testid={`lager-room-${room.id}`}
                                                >
                                                    <Folder className="h-3 w-3 shrink-0 text-secondary" />
                                                    <span className="truncate">{room.name}</span>
                                                    <ChevronRight className={`ml-auto h-3 w-3 shrink-0 ${expanded[`r-${room.id}`] ? "rotate-90" : ""}`} />
                                                </button>
                                                <NodeActions
                                                    addLabel="Regal hinzufügen"
                                                    onAdd={() => setAddNode({ kind: "shelf", parent: room })}
                                                    editLabel={`Raum ${room.name} bearbeiten`}
                                                    onEdit={() => setSelected({ kind: "room", doc: room })}
                                                    deleteLabel={`Raum ${room.name} löschen`}
                                                    onDelete={() => setDeleteNode({ kind: "room", doc: room })}
                                                    testId={`lager-room-actions-${room.id}`}
                                                />
                                            </div>
                                            {expanded[`r-${room.id}`] &&
                                                sortedShelves.filter((s) => s.room_id === room.id).map((sh) => (
                                                    <div key={sh.id} className="ml-5">
                                                        <div className="flex items-center gap-1">
                                                            <button
                                                                type="button"
                                                                onClick={() => toggle(`s-${sh.id}`)}
                                                                className="flex min-h-11 min-w-0 flex-1 items-center gap-2 text-xs text-foreground"
                                                                data-testid={`lager-shelf-${sh.id}`}
                                                            >
                                                                <Package className="h-3 w-3 shrink-0 text-accent" />
                                                                <span className="truncate">{sh.name}</span>
                                                                <span className="text-muted-foreground">({sh.code})</span>
                                                                <ChevronRight className={`ml-auto h-3 w-3 shrink-0 ${expanded[`s-${sh.id}`] ? "rotate-90" : ""}`} />
                                                            </button>
                                                            <NodeActions
                                                                addLabel="Fach hinzufügen"
                                                                onAdd={() => setAddNode({ kind: "compartment", parent: sh })}
                                                                editLabel={`Regal ${sh.name} bearbeiten`}
                                                                onEdit={() => setSelected({ kind: "shelf", doc: sh })}
                                                                deleteLabel={`Regal ${sh.name} löschen`}
                                                                onDelete={() => setDeleteNode({ kind: "shelf", doc: sh })}
                                                                testId={`lager-shelf-actions-${sh.id}`}
                                                            />
                                                        </div>
                                                        {expanded[`s-${sh.id}`] &&
                                                            sortedCompartments.filter((c) => c.shelf_id === sh.id).map((co) => (
                                                                <div key={co.id} className="ml-5 flex items-center gap-1">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setSelected({ kind: "compartment", doc: co })}
                                                                        className={`flex min-h-11 min-w-0 flex-1 items-center gap-2 rounded-md px-1 text-left text-xs text-foreground ${selected?.doc?.id === co.id ? "bg-primary/15 font-semibold" : ""}`}
                                                                        data-testid={`lager-comp-${co.id}`}
                                                                    >
                                                                        <span>·</span>
                                                                        <span className="min-w-0 break-words">
                                                                            <span className="block">{co.name}</span>
                                                                            <span className="block font-normal">({co.code})</span>
                                                                        </span>
                                                                        {co.is_staging ? (
                                                                            <span className="ml-auto flex items-center gap-1" title="Staging"><span className="h-2 w-2 rotate-45 bg-warning" aria-hidden="true" /><span className="text-xs uppercase tracking-wider text-warning">Staging</span></span>
                                                                        ) : null}
                                                                    </button>
                                                                    <NodeActions
                                                                        editLabel={`Fach ${co.name} bearbeiten`}
                                                                        onEdit={() => setSelected({ kind: "compartment", doc: co })}
                                                                        deleteLabel={`Fach ${co.name} löschen`}
                                                                        onDelete={() => setDeleteNode({ kind: "compartment", doc: co })}
                                                                        testId={`lager-comp-actions-${co.id}`}
                                                                    />
                                                                </div>
                                                            ))}
                                                    </div>
                                                ))}
                                        </div>
                                    ))}
                            </div>
                        ))
                    )}
                </aside>
                <div className="col-span-12 min-w-0 overflow-auto lg:col-span-7 lg:max-h-[calc(100dvh-14rem)]" aria-label="Lagerort bearbeiten">
                    {selected ? (
                        <StructureEditor kind={selected.kind} doc={selected.doc} onSave={saveNode} />
                    ) : (
                        <div className="rounded-md border border-border/60 bg-card/40 p-6 text-xs text-foreground/70">
                            Wähle im Baum den Bearbeiten-Stift an einem Standort, Raum, Regal oder Fach.
                        </div>
                    )}
                </div>
            </div>

            <CompartmentQrDialog
                open={qrOpen}
                onClose={() => setQrOpen(false)}
                items={qrItems}
                testId="lager-qr-dialog"
            />
            <AddLocationDialog
                open={showAddLocation}
                onClose={() => setShowAddLocation(false)}
                onCreated={() => { setShowAddLocation(false); load(); }}
            />
            <AddNodeDialog
                node={addNode}
                onClose={() => setAddNode(null)}
                onChanged={load}
                onCreated={(kind, parent, message) => {
                    setAddNode(null);
                    setCreateMessage(message);
                    // Eltern-Knoten aufklappen, damit das neue Element sichtbar ist.
                    setExpanded((e) => ({
                        ...e,
                        [kind === "room" ? `l-${parent.id}` : kind === "shelf" ? `r-${parent.id}` : `s-${parent.id}`]: true,
                    }));
                    load();
                }}
            />
            <AlertDialog open={!!deleteNode} onOpenChange={(v) => !v && setDeleteNode(null)}>
                <AlertDialogContent data-testid="lager-delete-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {deleteNode ? `${DELETE_META[deleteNode.kind].label} „${deleteNode.doc.name}" löschen?` : ""}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {deleteNode?.kind === "compartment"
                                ? "Nur möglich, wenn dieses Fach keine Bestands-Positionen mehr führt — Bestände zuerst umlagern."
                                : deleteNode
                                    ? `Löscht auch ${DELETE_META[deleteNode.kind].cascade}. Nur möglich, wenn kein betroffenes Fach mehr Bestands-Positionen führt — Bestände zuerst umlagern.`
                                    : ""}{" "}
                            Das Löschen ist nicht umkehrbar.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="lager-delete-cancel">Abbrechen</AlertDialogCancel>
                        <AlertDialogAction data-testid="lager-delete-submit" onClick={runDelete}>
                            Löschen
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}

// Kleine Icon-Aktionen am Baumknoten -- außerhalb des Toggle-Buttons, damit
// kein Button im Button entsteht (a11y).
function NodeActions({ addLabel, onAdd, editLabel, onEdit, deleteLabel, onDelete, testId }) {
    return (
        <span className="flex shrink-0 items-center" data-testid={testId}>
            {onAdd ? (
                <button
                    type="button"
                    onClick={onAdd}
                    aria-label={addLabel}
                    title={addLabel}
                    className="flex h-11 w-11 items-center justify-center rounded-md text-foreground/70 hover:bg-card hover:text-foreground"
                    data-testid={`${testId}-add`}
                >
                    <Plus className="h-3.5 w-3.5" />
                </button>
            ) : null}
            {onEdit ? (
                <button
                    type="button"
                    onClick={onEdit}
                    aria-label={editLabel}
                    title={editLabel}
                    className="flex h-11 w-11 items-center justify-center rounded-md text-foreground/70 hover:bg-card hover:text-foreground"
                    data-testid={`${testId}-edit`}
                >
                    <Pencil className="h-3.5 w-3.5" />
                </button>
            ) : null}
            <button
                type="button"
                onClick={onDelete}
                aria-label={deleteLabel}
                title={deleteLabel}
                className="flex h-11 w-11 items-center justify-center rounded-md text-foreground/70 hover:bg-card hover:text-destructive"
                data-testid={`${testId}-delete`}
            >
                <Trash2 className="h-3.5 w-3.5" />
            </button>
        </span>
    );
}

const ADD_META = {
    room: { title: "Neuer Raum", parentLabel: "Standort", hasCode: false },
    shelf: { title: "Neues Regal", parentLabel: "Raum", hasCode: true },
    compartment: { title: "Neues Fach", parentLabel: "Regal", hasCode: true },
};

function AddNodeDialog({ node, onClose, onCreated, onChanged }) {
    const [name, setName] = useState("");
    const [code, setCode] = useState("");
    const [isStaging, setIsStaging] = useState(false);
    const [errors, setErrors] = useState({});
    const [busy, setBusy] = useState(false);
    const [batch, setBatch] = useState(false);
    const [start, setStart] = useState("1");
    const [end, setEnd] = useState("");
    const [serverError, setServerError] = useState("");
    const requestRef = useRef(null);
    const busyRef = useRef(false);

    useEffect(() => {
        if (node) {
            setName("");
            setCode("");
            setIsStaging(false);
            setErrors({});
            setBatch(false);
            setStart("1");
            setEnd("");
            setServerError("");
            requestRef.current = null;
        }
    }, [node]);

    if (!node) return null;
    const meta = ADD_META[node.kind];
    const first = Number(start);
    const last = Number(end);
    const count = last - first + 1;
    const rangeValid = /^\d+$/.test(start) && /^\d+$/.test(end)
        && first >= 1 && last <= 999999 && count >= 1 && count <= 500;
    const preview = name.trim() && rangeValid;

    const submit = async (event) => {
        event.preventDefault();
        if (busyRef.current) return;
        const required = "Pflichtfeld.";
        const next = {};
        if (!name.trim()) next.name = required;
        if (batch && name.trim().length > 200) next.name = "Bitte höchstens 200 Zeichen verwenden.";
        if (meta.hasCode && !batch && !code.trim()) next.code = required;
        if (batch && !rangeValid) next.range = "Ganze Zahlen von 1 bis 999999 eingeben. Ende mindestens wie Beginn; höchstens 500 Fächer pro Vorgang.";
        setErrors(next);
        setServerError("");
        if (Object.keys(next).length > 0) return;
        busyRef.current = true;
        setBusy(true);
        let res;
        if (batch) {
            const payload = { shelf_id: node.parent.id, name: name.trim(), start: first, end: last, is_staging: isStaging };
            const signature = JSON.stringify(payload);
            if (requestRef.current?.signature !== signature) {
                requestRef.current = { signature, id: crypto.randomUUID() };
            }
            res = await rpc.post("/master/compartments/batch", { ...payload, request_id: requestRef.current.id });
        } else if (node.kind === "room") {
            res = await rpc.post("/master/rooms", { location_id: node.parent.id, name: name.trim() });
        } else if (node.kind === "shelf") {
            res = await rpc.post("/master/shelves", { room_id: node.parent.id, name: name.trim(), code: code.trim() });
        } else {
            res = await rpc.post("/master/compartments", {
                shelf_id: node.parent.id,
                name: name.trim(),
                code: code.trim(),
                is_staging: isStaging,
            });
        }
        busyRef.current = false;
        setBusy(false);
        if (!res.ok) {
            setServerError(batch && !res.status
                ? "Keine Bestätigung vom Server erhalten. Bitte den Dialog offen lassen und mit unveränderten Eingaben erneut versuchen; bereits angelegte Fächer werden nicht dupliziert."
                : res.error);
            if (batch) onChanged();
            return;
        }
        const message = batch
            ? `${res.data.count} ${res.data.count === 1 ? "Fach angelegt" : "Fächer angelegt"}.`
            : `${meta.title.replace("Neuer ", "").replace("Neues ", "")} angelegt.`;
        onCreated(node.kind, node.parent, message);
    };

    return (
        <Dialog open={!!node} onOpenChange={(v) => !v && !busyRef.current && onClose()}>
            <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto [&_input]:h-11 [&_input]:border-foreground/60 [&_input:focus-visible]:ring-foreground [&_button:focus-visible]:ring-foreground" data-testid="lager-add-node-dialog">
                <DialogHeader>
                    <DialogTitle>{batch ? "Mehrere Fächer anlegen" : meta.title}</DialogTitle>
                    <DialogDescription className="text-foreground">{meta.parentLabel}: {node.parent.name}</DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} noValidate>
                  <fieldset disabled={busy} className="flex min-w-0 flex-col gap-3">
                    {node.kind === "compartment" ? (
                        <label className="flex min-h-11 items-center gap-3 text-sm">
                            <Checkbox className="border-foreground/60 data-[state=checked]:text-foreground dark:data-[state=checked]:text-background" checked={batch} onCheckedChange={(v) => { setBatch(!!v); setErrors({}); setServerError(""); }} />
                            Mehrere Fächer anlegen
                        </label>
                    ) : null}
                    <Label htmlFor="lager-node-name">Name *</Label>
                    <Input
                        id="lager-node-name"
                        autoFocus
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        aria-invalid={!!errors.name}
                        aria-describedby={errors.name ? "lager-node-name-error" : undefined}
                        data-testid="lager-node-name"
                    />
                    {errors.name ? (
                        <p role="alert" id="lager-node-name-error" className="text-sm text-foreground" data-testid="lager-node-name-error">{errors.name}</p>
                    ) : null}
                    {batch ? (
                        <>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                <div className="space-y-2">
                                    <Label htmlFor="lager-batch-start">Beginnend mit Nummer</Label>
                                    <Input id="lager-batch-start" type="number" inputMode="numeric" min="1" max="999999" step="1"
                                        value={start} onChange={(e) => setStart(e.target.value)} aria-invalid={!!errors.range}
                                        aria-describedby={errors.range ? "lager-batch-range-error" : "lager-batch-hint"} />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="lager-batch-end">Endend mit Nummer</Label>
                                    <Input id="lager-batch-end" type="number" inputMode="numeric" min="1" max="999999" step="1"
                                        value={end} onChange={(e) => setEnd(e.target.value)} aria-invalid={!!errors.range}
                                        aria-describedby={errors.range ? "lager-batch-range-error" : "lager-batch-hint"} />
                                </div>
                            </div>
                            <p id="lager-batch-hint" className="text-sm text-foreground">Beide Nummern eingeschlossen. Höchstens 500 Fächer pro Vorgang.</p>
                            {errors.range ? <p role="alert" id="lager-batch-range-error" data-testid="lager-batch-range-error" className="text-sm text-foreground">{errors.range}</p> : null}
                            {preview ? (
                                <div className="space-y-1 rounded-md bg-card p-3 text-sm text-foreground" data-testid="lager-batch-preview" role="status">
                                    <p className="font-semibold">Vorschau: {count} {count === 1 ? "Fach" : "Fächer"}</p>
                                    <p className="break-words">Fachname: {name.trim()} {first}{count > 1 ? ` bis ${name.trim()} ${last}` : ""}</p>
                                    <p className="break-words">Code: {node.parent.name.trim()} {name.trim()} {first}</p>
                                    {count > 1 ? <p className="break-words">bis {node.parent.name.trim()} {name.trim()} {last}</p> : null}
                                </div>
                            ) : null}
                        </>
                    ) : null}
                    {meta.hasCode && !batch ? (
                        <>
                            <Label htmlFor="lager-node-code">Code *</Label>
                            <Input
                                id="lager-node-code"
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                                aria-invalid={!!errors.code}
                                aria-describedby={errors.code ? "lager-node-code-error" : undefined}
                                data-testid="lager-node-code"
                            />
                            {errors.code ? (
                                <p role="alert" id="lager-node-code-error" className="text-sm text-foreground" data-testid="lager-node-code-error">{errors.code}</p>
                            ) : null}
                        </>
                    ) : null}
                    {node.kind === "compartment" ? (
                        <label className="flex min-h-11 items-center gap-3 text-sm">
                            <Checkbox
                                checked={isStaging}
                                onCheckedChange={(v) => setIsStaging(!!v)}
                                data-testid="lager-node-staging"
                                className="border-foreground/60 data-[state=checked]:text-foreground dark:data-[state=checked]:text-background"
                            />
                            Staging-Fach (Zwischenplatz, zählt nicht als final eingelagert)
                        </label>
                    ) : null}
                    {serverError ? <p role="alert" className="text-sm text-foreground" data-testid="lager-node-server-error">{serverError}</p> : null}
                    <Button type="submit" disabled={busy} data-testid="lager-node-submit" className="min-h-11 border border-foreground/60 text-foreground dark:text-background">
                        {busy ? "Wird angelegt …" : batch && preview ? `${count} ${count === 1 ? "Fach" : "Fächer"} anlegen` : "Anlegen"}
                    </Button>
                  </fieldset>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function AddLocationDialog({ open, onClose, onCreated }) {
    const [name, setName] = useState("");
    const [fieldError, setFieldError] = useState("");
    const [busy, setBusy] = useState(false);
    const submit = async (event) => {
        event.preventDefault();
        if (!name.trim()) return setFieldError("Pflichtfeld.");
        setFieldError("");
        setBusy(true);
        const res = await rpc.post("/master/locations", { name: name.trim() });
        setBusy(false);
        if (!res.ok) return toast.error(res.error);
        toast.success("Standort angelegt.");
        setName("");
        onCreated();
    };
    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent data-testid="lager-add-location-dialog">
                <DialogHeader><DialogTitle>Neuer Standort</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="flex flex-col gap-3">
                    <Label htmlFor="lager-location-name">Standortname *</Label>
                    <Input
                        id="lager-location-name"
                        autoFocus
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        aria-invalid={!!fieldError}
                        data-testid="lager-location-name"
                    />
                    {fieldError ? (
                        <p className="text-xs text-destructive" data-testid="lager-location-name-error">{fieldError}</p>
                    ) : null}
                    <Button type="submit" disabled={busy} data-testid="lager-location-submit">
                        {busy ? "..." : "Anlegen"}
                    </Button>
                </form>
            </DialogContent>
        </Dialog>
    );
}

const EDIT_META = {
    location: { label: "Standort", prefix: "location", hasCode: false },
    room: { label: "Raum", prefix: "room", hasCode: false },
    shelf: { label: "Regal", prefix: "shelf", hasCode: true },
    compartment: { label: "Fach", prefix: "comp", hasCode: true },
};

function StructureEditor({ kind, doc, onSave }) {
    const meta = EDIT_META[kind];
    const [form, setForm] = useState(doc);
    const [errors, setErrors] = useState({});
    const [message, setMessage] = useState("");
    const [serverError, setServerError] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        setForm(doc);
        setErrors({});
        setMessage("");
        setServerError("");
    }, [doc]);

    const submit = async (event) => {
        event.preventDefault();
        const nextErrors = {};
        if (!form.name?.trim()) nextErrors.name = "Pflichtfeld.";
        if (meta.hasCode && !form.code?.trim()) nextErrors.code = "Pflichtfeld.";
        setErrors(nextErrors);
        setMessage("");
        setServerError("");
        if (Object.keys(nextErrors).length > 0) return;

        const patch = { name: form.name.trim() };
        if (meta.hasCode) patch.code = form.code.trim();
        if (kind === "compartment") {
            patch.capacity = form.capacity ?? null;
            patch.is_staging = !!form.is_staging;
        }

        setBusy(true);
        const res = await onSave(kind, doc, patch);
        setBusy(false);
        if (!res.ok) return setServerError(res.error);
        setForm(res.data);
        setMessage(res.message);
    };

    return (
        <form
            onSubmit={submit}
            className="rounded-lg border border-border bg-card/40 p-5"
            data-testid={`lager-${meta.prefix}-editor`}
        >
            <h3 className="text-lg font-semibold text-foreground">{meta.label} bearbeiten</h3>
            {doc.code ? <p className="mt-0 mb-4 text-xs text-foreground/70">{doc.code}</p> : null}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                    <Label htmlFor={`${meta.prefix}-edit-name`}>Name *</Label>
                    <Input
                        id={`${meta.prefix}-edit-name`}
                        value={form.name || ""}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        aria-invalid={!!errors.name}
                        aria-describedby={errors.name ? `${meta.prefix}-edit-name-error` : undefined}
                        className="h-11"
                        data-testid={`${meta.prefix}-edit-name`}
                    />
                    {errors.name ? (
                        <p id={`${meta.prefix}-edit-name-error`} className="mt-1 text-xs text-destructive" data-testid={`${meta.prefix}-edit-name-error`}>
                            {errors.name}
                        </p>
                    ) : null}
                </div>
                {meta.hasCode ? (
                    <div>
                        <Label htmlFor={`${meta.prefix}-edit-code`}>Code *</Label>
                        <Input
                            id={`${meta.prefix}-edit-code`}
                            value={form.code || ""}
                            onChange={(e) => setForm({ ...form, code: e.target.value })}
                            aria-invalid={!!errors.code}
                            aria-describedby={errors.code ? `${meta.prefix}-edit-code-error` : undefined}
                            className="h-11"
                            data-testid={`${meta.prefix}-edit-code`}
                        />
                        {errors.code ? (
                            <p id={`${meta.prefix}-edit-code-error`} className="mt-1 text-xs text-destructive" data-testid={`${meta.prefix}-edit-code-error`}>
                                {errors.code}
                            </p>
                        ) : null}
                    </div>
                ) : null}
                {kind === "compartment" ? (
                    <>
                        <div>
                            <Label htmlFor="comp-edit-capacity">Kapazität</Label>
                            <Input
                                id="comp-edit-capacity"
                                type="number"
                                value={form.capacity ?? ""}
                                onChange={(e) => setForm({ ...form, capacity: e.target.value === "" ? null : Number(e.target.value) })}
                                className="h-11"
                                data-testid="comp-edit-capacity"
                            />
                        </div>
                        <div className="flex items-end gap-2">
                            <label className="flex min-h-11 items-center gap-2 text-sm">
                                <Checkbox
                                    checked={!!form.is_staging}
                                    onCheckedChange={(v) => setForm({ ...form, is_staging: !!v })}
                                    data-testid="comp-edit-staging"
                                />
                                Staging-Fach
                            </label>
                        </div>
                    </>
                ) : null}
            </div>
            {kind === "compartment" ? (
                <div className="mt-2 text-xs text-foreground/80">
                    Kompatibilitätscode für ältere Etiketten: <code>{form.qr_content}</code>
                </div>
            ) : null}
            {serverError ? (
                <p className="mt-3 text-sm text-destructive" role="alert" data-testid={`${meta.prefix}-edit-error`}>
                    {serverError}
                </p>
            ) : null}
            {message ? (
                <p className="mt-3 text-sm text-foreground" role="status" data-testid={`${meta.prefix}-edit-status`}>
                    {message}
                </p>
            ) : null}
            <Button
                type="submit"
                className="mt-4 h-11 text-foreground dark:text-background"
                disabled={busy}
                data-testid={`${meta.prefix}-edit-save`}
            >
                {busy ? "..." : "Speichern"}
            </Button>
        </form>
    );
}
